import { useState } from 'react';
import type { TableInfo, PermissionState } from '@/types/admin';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { fetchTableColumns } from '@/services/admin';
import {
  ChevronRight,
  ChevronDown,
  Eye,
  Edit,
  Trash2,
  Check,
  Search,
  Settings,
  Shield,
} from 'lucide-react';
import { cn } from '@/utils';

interface PermissionManagerProps {
  permissions: PermissionState;
  tables: TableInfo[];
  onChange: (permissions: PermissionState) => void;
  disabled?: boolean;
}

export function PermissionManager({
  permissions,
  tables,
  onChange,
  disabled,
}: PermissionManagerProps) {
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set());
  const [tableColumns, setTableColumns] = useState<Record<string, string[]>>({});
  const [loadingColumns, setLoadingColumns] = useState<Set<string>>(new Set());
  const [searchField, setSearchField] = useState('');

  // Toggle table expansion
  const toggleTable = async (tableName: string) => {
    const newExpanded = new Set(expandedTables);

    if (newExpanded.has(tableName)) {
      newExpanded.delete(tableName);
      setExpandedTables(newExpanded);
    } else {
      newExpanded.add(tableName);
      setExpandedTables(newExpanded);

      // Load columns if not already loaded
      if (!tableColumns[tableName] && !loadingColumns.has(tableName)) {
        setLoadingColumns((prev) => new Set([...prev, tableName]));
        try {
          const columns = await fetchTableColumns(tableName);
          setTableColumns((prev) => ({ ...prev, [tableName]: columns }));
        } catch (error) {
          console.error('Error loading columns:', error);
          // Use default columns
          setTableColumns((prev) => ({
            ...prev,
            [tableName]: ['id', 'created_at', 'updated_at'],
          }));
        } finally {
          setLoadingColumns((prev) => {
            const next = new Set(prev);
            next.delete(tableName);
            return next;
          });
        }
      }
    }
  };

  // Enable/disable table access
  const toggleTableEnabled = (tableName: string, enabled: boolean) => {
    const current = permissions[tableName] || {
      enabled: false,
      read: false,
      write: false,
      delete: false,
      fields: [],
    };

    onChange({
      ...permissions,
      [tableName]: {
        ...current,
        enabled,
        fields: enabled ? current.fields : [],
      },
    });
  };

  // Toggle permission
  const togglePermission = (
    tableName: string,
    permission: 'read' | 'write' | 'delete'
  ) => {
    const current = permissions[tableName] || {
      enabled: false,
      read: false,
      write: false,
      delete: false,
      fields: [],
    };

    onChange({
      ...permissions,
      [tableName]: {
        ...current,
        enabled: true,
        [permission]: !current[permission],
      },
    });
  };

  // Toggle field access
  const toggleField = (tableName: string, fieldName: string) => {
    const current = permissions[tableName] || {
      enabled: false,
      read: false,
      write: false,
      delete: false,
      fields: [],
    };

    const fields = current.fields.includes(fieldName)
      ? current.fields.filter((f) => f !== fieldName)
      : [...current.fields, fieldName];

    onChange({
      ...permissions,
      [tableName]: {
        ...current,
        enabled: true,
        fields,
      },
    });
  };

  // Select/deselect all fields
  const toggleAllFields = (tableName: string, select: boolean) => {
    const columns = tableColumns[tableName] || [];
    const current = permissions[tableName] || {
      enabled: false,
      read: false,
      write: false,
      delete: false,
      fields: [],
    };

    onChange({
      ...permissions,
      [tableName]: {
        ...current,
        enabled: true,
        fields: select ? [...columns] : [],
      },
    });
  };

  // Get columns for a table (with fallback)
  const getColumns = (tableName: string) => {
    return tableColumns[tableName] || ['id', 'created_at', 'updated_at'];
  };

  // Filter columns based on search
  const getFilteredColumns = (tableName: string) => {
    const columns = getColumns(tableName);
    if (!searchField) return columns;
    return columns.filter((col) =>
      col.toLowerCase().includes(searchField.toLowerCase())
    );
  };

  // Count enabled permissions
  const getEnabledCount = (tableName: string) => {
    const perm = permissions[tableName];
    if (!perm?.enabled) return 0;
    let count = 0;
    if (perm.read) count++;
    if (perm.write) count++;
    if (perm.delete) count++;
    return count;
  };

  if (tables.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No tables found in the database.</p>
        <p className="text-sm">Create tables in Supabase to assign permissions.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Fields */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search fields..."
          value={searchField}
          onChange={(e) => setSearchField(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table List */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto">
        {tables.map((table) => {
          const isExpanded = expandedTables.has(table.table_name);
          const perm = permissions[table.table_name] || {
            enabled: false,
            read: false,
            write: false,
            delete: false,
            fields: [],
          };
          const enabledCount = getEnabledCount(table.table_name);
          const columns = getFilteredColumns(table.table_name);

          return (
            <div
              key={table.table_name}
              className={cn(
                'border rounded-lg overflow-hidden',
                perm.enabled && 'border-primary/50 bg-primary/5'
              )}
            >
              {/* Table Header */}
              <div
                className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/50"
                onClick={() => !disabled && toggleTable(table.table_name)}
              >
                <button
                  className="p-1 hover:bg-muted rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleTable(table.table_name);
                  }}
                  disabled={disabled}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>

                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{table.table_name}</p>
                  {perm.enabled && enabledCount > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {enabledCount}/3 permissions
                      {perm.fields.length > 0 && `, ${perm.fields.length} fields`}
                    </p>
                  )}
                </div>

                {/* Enable Toggle */}
                {!disabled && (
                  <button
                    className={cn(
                      'w-6 h-6 rounded border-2 flex items-center justify-center transition-colors',
                      perm.enabled
                        ? 'bg-primary border-primary text-primary-foreground'
                        : 'border-muted-foreground/30 hover:border-primary/50'
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleTableEnabled(table.table_name, !perm.enabled);
                    }}
                  >
                    {perm.enabled && <Check className="w-4 h-4" />}
                  </button>
                )}

                {perm.enabled && (
                  <Badge variant="success" className="text-xs">
                    Active
                  </Badge>
                )}
              </div>

              {/* Expanded Permissions Panel */}
              {isExpanded && (
                <div className="border-t bg-background p-3">
                  {/* CRUD Permissions */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <button
                      className={cn(
                        'flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors',
                        perm.read
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'border-muted hover:border-primary/50'
                      )}
                      onClick={() => togglePermission(table.table_name, 'read')}
                      disabled={disabled || !perm.enabled}
                    >
                      <Eye className="w-4 h-4" />
                      <span className="text-sm">Read</span>
                      {perm.read && <Check className="w-3 h-3" />}
                    </button>

                    <button
                      className={cn(
                        'flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors',
                        perm.write
                          ? 'bg-green-50 border-green-300 text-green-700'
                          : 'border-muted hover:border-primary/50'
                      )}
                      onClick={() => togglePermission(table.table_name, 'write')}
                      disabled={disabled || !perm.enabled}
                    >
                      <Edit className="w-4 h-4" />
                      <span className="text-sm">Write</span>
                      {perm.write && <Check className="w-3 h-3" />}
                    </button>

                    <button
                      className={cn(
                        'flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-colors',
                        perm.delete
                          ? 'bg-red-50 border-red-300 text-red-700'
                          : 'border-muted hover:border-primary/50'
                      )}
                      onClick={() => togglePermission(table.table_name, 'delete')}
                      disabled={disabled || !perm.enabled}
                    >
                      <Trash2 className="w-4 h-4" />
                      <span className="text-sm">Delete</span>
                      {perm.delete && <Check className="w-3 h-3" />}
                    </button>
                  </div>

                  {/* Field Permissions */}
                  {perm.read && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium flex items-center gap-2">
                          <Settings className="w-4 h-4" />
                          Visible Fields ({perm.fields.length}/{columns.length})
                        </p>
                        <div className="flex gap-2">
                          <button
                            className="text-xs text-primary hover:underline"
                            onClick={() => toggleAllFields(table.table_name, true)}
                            disabled={disabled}
                          >
                            Select All
                          </button>
                          <button
                            className="text-xs text-muted-foreground hover:underline"
                            onClick={() => toggleAllFields(table.table_name, false)}
                            disabled={disabled}
                          >
                            Deselect All
                          </button>
                        </div>
                      </div>

                      {loadingColumns.has(table.table_name) ? (
                        <div className="flex items-center justify-center py-4 text-muted-foreground">
                          <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin mr-2" />
                          Loading columns...
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-1 max-h-48 overflow-y-auto">
                          {columns.map((column) => {
                            const isSelected = perm.fields.includes(column);
                            return (
                              <label
                                key={column}
                                className={cn(
                                  'flex items-center gap-2 px-2 py-1 rounded cursor-pointer transition-colors',
                                  isSelected
                                    ? 'bg-primary/10'
                                    : 'hover:bg-muted'
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleField(table.table_name, column)}
                                  className="rounded border-muted-foreground/30"
                                  disabled={disabled}
                                />
                                <span className="text-sm truncate">{column}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {!perm.read && perm.enabled && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Enable "Read" permission to select visible fields
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
